import React from "react";
import SwitchSelect from "features/bots/BotForm/common/SwitchSelect";
import Select from "common/Select/Select";
import { FormFieldTitle } from "features/bots/common/styles";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import useMarginSelectors from "@shared/features/bots/BotForm/useMarginSelectors";
import { UseMarginForm } from "@shared/features/bots/BotForm/common/types";
import { openMessage } from "utils/misc";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types/common/common.types";
import useSignalLeverageWarningModal from "features/tradingTerminal/General/useSignalLeverageWarningModal.tsx";
import { translateOptionName } from "@shared/common/i18n/optionNames";

const useMarginForm: UseMarginForm = () => {
  const {
    multiplierData,
    longShortData,
    marginTypeData,
    warnings,
    gridBotDirectionChangeModalData,
    isAdvancedView,
    isSignalsBot,
    isTradingViewSignalsBot,
  } = useMarginSelectors({ openMessage });
  const { onMultiplierChange, leverageWarningModal } =
    useSignalLeverageWarningModal({ setMultiplier: multiplierData.onChange });

  const getLongShortSelect = () => {
    if (!longShortData.shouldDisplay || (isSignalsBot && !isAdvancedView))
      return;
    return (
      <div key="longShort">
        <FormFieldTitle>
          {longShortData.title}
          <HelperTooltip {...longShortData.helperData} />
        </FormFieldTitle>
        {longShortData.isDropdown ? (
          <Select
            style={{ width: "100%" }}
            value={longShortData.defaultValue as any}
            onChange={longShortData.onChange}
            popupMatchSelectWidth={false}
            optionLabelProp="label"
            disabled={longShortData.disabled}
            placeholder={longShortData.placeholder}
            mode={longShortData.isMultiSelect ? "multiple" : undefined}
          >
            {longShortData.options?.map((option) => {
              return (
                <Select.Option value={option} key={option} label={option}>
                  {option}
                </Select.Option>
              );
            })}
          </Select>
        ) : (
          <SwitchSelect
            options={longShortData.options}
            optionToDisplayName={Object.fromEntries(
              longShortData.options.map((option) => [
                option,
                translateOptionName(option),
              ])
            )}
            selectedOption={longShortData.defaultValue as any}
            onClick={longShortData.onChange}
            disabled={longShortData.disabled}
          />
        )}
      </div>
    );
  };
  const longShortSelect = getLongShortSelect();

  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const getMarginTypeSelect = () => {
    if (!marginTypeData.shouldDisplay) return;
    return (
      <div key="marginType">
        <FormFieldTitle>{t("labels.margin_type")}</FormFieldTitle>
        <Select
          style={{ width: "100%" }}
          value={marginTypeData.value as any}
          onChange={marginTypeData.onChange}
          popupMatchSelectWidth={false}
          optionLabelProp="label"
          disabled={marginTypeData.disabled}
          placeholder={marginTypeData.placeholder}
        >
          {marginTypeData.options?.map((option) => {
            return (
              <Select.Option
                value={option.value}
                key={option.value}
                label={option.name}
              >
                {option.name}
              </Select.Option>
            );
          })}
        </Select>
      </div>
    );
  };

  const marginTypeSelect = getMarginTypeSelect();

  const getMultiplierSelect = () => {
    if (!multiplierData.shouldDisplay) return;
    return (
      <div key="multiplier">
        <FormFieldTitle>{t("labels.multiplier")}</FormFieldTitle>
        <Select
          style={{ width: "100%" }}
          value={multiplierData.value}
          onChange={
            isTradingViewSignalsBot
              ? onMultiplierChange
              : multiplierData.onChange
          }
          disabled={multiplierData.disabled}
          placeholder={multiplierData.placeholder}
          options={multiplierData.options}
        />
      </div>
    );
  };
  const multiplierSelect = getMultiplierSelect();

  const getWarningItem = (text: string, isError: boolean, key: number) => {
    return (
      <Text
        as="div"
        type={isError ? "danger" : "warning"}
        $size={typography.fontSizes.S}
        style={{ marginTop: 2 }}
        key={key}
      >
        {text}
      </Text>
    );
  };

  const getWarning = () => {
    const warningContent = warnings?.map(([warning, isError], index) =>
      getWarningItem(warning, isError, index)
    );
    return warningContent?.length ? (
      <div style={{ marginTop: 8 }}>{warningContent}</div>
    ) : undefined;
  };

  return {
    longShortSelect,
    marginTypeSelect,
    multiplierSelect,
    gridBotDirectionChangeModalData,
    warning: getWarning(),
    showLeverageSection:
      !!longShortData.shouldDisplay || !!marginTypeSelect || !!multiplierSelect,
    leverageWarningModal: isTradingViewSignalsBot
      ? leverageWarningModal
      : undefined,
  };
};

export default useMarginForm;
